import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  TableCell,
  TableRow,
  FormControl,
  FormHelperText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const RecipientTableRow = ({
  recipient,
  notificationTypes,
  isEditing,
  editValue,
  editError,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onEditChange,
  isSaving,
}) => {
  const [selectedTypes, setSelectedTypes] = useState(
    () => recipient.categories?.map((c) => c.id) ?? []
  );

  useEffect(() => {
    if (isEditing) {
      setSelectedTypes(recipient.categories?.map((c) => c.id) ?? []);
    }
  }, [isEditing]);

  return (
    <TableRow hover>
      <TableCell>
        {isEditing ? (
          <FormControl fullWidth error={!!editError}>
            <TextField
              fullWidth
              value={editValue}
              onChange={onEditChange}
              size="small"
              autoFocus
              error={!!editError}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSaveEdit(selectedTypes);
                } else if (e.key === 'Escape') {
                  onCancelEdit();
                }
              }}
            />
            {editError && <FormHelperText>{editError}</FormHelperText>}
          </FormControl>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {recipient.recipientEmail}
          </Box>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Select
            multiple
            value={selectedTypes}
            onChange={(e) => setSelectedTypes(e.target.value)}
            size="small"
            fullWidth
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const type = (notificationTypes ?? []).find((t) => t.id === id);
                  return <Chip key={id} label={type?.name} size="small" />;
                })}
              </Box>
            )}
          >
            {(notificationTypes ?? []).map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                <Checkbox checked={selectedTypes.includes(id)} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {recipient.categories?.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            ) : (
              recipient.categories?.map((cat) => (
                <Chip key={cat.id} label={cat.name} size="small" variant="outlined" />
              ))
            )}
          </Box>
        )}
      </TableCell>
      <TableCell align="right">
        {isEditing ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => onSaveEdit(selectedTypes)}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={isSaving}>
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton size="small" color="primary" onClick={onStartEdit}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

export default RecipientTableRow;
